import { Column } from "@/components/data-table";
import { Form, useSchemaForm } from "@/components/form";
import { Dialog, DialogProps, ModalHeader } from "@/components/modal";
import { BaseQuery, QueryResponse } from "@/shared/types/base";
import PrintComponent from "./print-component";
import { useFiltersReturn } from "@/components/filters";
import { usePrinter } from "@/components/print";
import { z } from "zod/v4";
import { ReactNode } from "react";

type AdditionalDisplayDataEntry = { label: ReactNode, value: ReactNode };

interface PrintExportDialogDataProps<T = any> {
    dialogTitle?: string,
    defaultFileTitle: string,
    fetchFn: (query?: BaseQuery) => Promise<QueryResponse<T>>
    filterData: useFiltersReturn,
    columns: Column<T>[],
    additionalDisplayData?: AdditionalDisplayDataEntry[] | ((response: QueryResponse<T>) => AdditionalDisplayDataEntry[])
}

export interface PrintExportDialogProps<T = any> extends DialogProps, PrintExportDialogDataProps<T> {
}

export default function PrintExportDialog<T = any>({
    fetchFn,
    columns,
    filterData,
    defaultFileTitle,
    dialogTitle = "Imprimir",
    additionalDisplayData,
    ...props
}: PrintExportDialogProps<T>) {
    return <Dialog size="sm" {...props}>
        <PrintExportDialogContent {...{
            fetchFn,
            columns,
            filterData,
            defaultFileTitle,
            dialogTitle,
            additionalDisplayData
        }} />
    </Dialog>
}

const printFormSchema = z.object({
    title: z.string().nonempty("Este campo es requerido"),
    description: z.string()
})

function PrintExportDialogContent({
    fetchFn,
    columns: columns,
    filterData,
    defaultFileTitle,
    dialogTitle = "Imprimir",
    additionalDisplayData
}: PrintExportDialogDataProps) {
    const printer = usePrinter();

    const printForm = useSchemaForm({
        schema: printFormSchema,
        onSubmit: async ({ value }) => {
            //Get query without pagination
            const queryWithoutPagination = filterData.filters.reduce((prev, filter) => {
                if (filter.type === "pagination") {
                    delete prev[filter.pageName];
                    delete prev[filter.pageSizeName];
                }
                return prev;
            }, { ...filterData.filterData });

            //Fetch data
            const response = await fetchFn(queryWithoutPagination);

            //Fetch model filter Data
            const modelFilters = filterData.filters.filter(f => f.type == "model");
            const additionalDataPromises = modelFilters.reduce((prev, mf) => {
                if (mf.value) prev[mf.name] = mf.get(mf.value);
                return prev;
            }, {} as Record<string, Promise<any>>);

            const additionalData = {} as Record<string, any>;
            for (const [name, promise] of Object.entries(additionalDataPromises)) {
                additionalData[name] = await promise;
            }

            //Additional Display Data
            const additionalDisplay = typeof additionalDisplayData == "function" ?
                additionalDisplayData(response) :
                additionalDisplayData;

            //Print
            await printer.print({
                documentTitle: value.title,
                printComponent: <PrintComponent
                    title={value.title}
                    description={value.description}
                    data={response.result}
                    additionalData={additionalData}
                    columns={columns}
                    filterData={filterData}
                    additionalDisplay={additionalDisplay}
                />
            })
        },
        defaultValues: {
            title: defaultFileTitle,
            description: ""
        },
        submitOnce: true,
        closeModalOnSubmit: true,
    })

    return <>
        <ModalHeader>{dialogTitle}</ModalHeader>
        <Form form={printForm} padding={3} gap={3}>
            <Form.TextField name="title" label="Título" />
            <Form.TextField name="description" label="Descripción" multiline rows={5} />
            <Form.SubmitButton>Exportar</Form.SubmitButton>
        </Form>
        <printer.Printer />
    </>
}